import { isXmattersEndpointSupportEnabled } from 'selectors/featureToggles';
import { EVERBRIDGE_LOGO_PATH } from '@xmatters/common/constants/image';

const BASIC = 'BASIC';
const OAUTH2_GOOGLE = 'OAUTH2_GOOGLE';
const NO_AUTH = 'NO_AUTH';
const OAUTH2 = 'OAUTH2';
const OAUTH2_AUTH_CODE = 'OAUTH2_AUTH_CODE';
const REMEDY_TOKEN = 'REMEDY_TOKEN';
const OAUTH2_FORCE = 'OAUTH2_FORCE';
const SERVICENOW = 'SERVICENOW';
const OAUTH_SLACK = 'OAUTH_SLACK';
const OAUTH_SLACK_V2 = 'OAUTH_SLACK_V2';
const OAUTH_ZOOM = 'OAUTH_ZOOM';
const OAUTH_ZOOM_S2S = 'OAUTH_ZOOM_S2S';
const OAUTH2_MS_GRAPH = 'OAUTH2_MS_GRAPH';
const TOKEN = 'TOKEN';
const XMATTERS = 'XMATTERS';
const EBFD = 'EBFD';
const KEY_LOGO_PATH = '/static/images/icons/key_12.png';

export const featureFlags = {
  [OAUTH2_GOOGLE]: 'GOOGLE_CLOUD_INTEGRATION',
  [OAUTH_SLACK_V2]: 'SLACK_OAUTH_V2_ENDPOINT',
  [OAUTH_ZOOM]: 'ZOOM_ENDPOINT',
  [OAUTH_ZOOM_S2S]: 'ENDPOINT_ZOOM_S2S',
  [OAUTH2_AUTH_CODE]: 'ENDPOINT_OAUTH2_AUTH_CODE_FLOW',
  [TOKEN]: 'ENDPOINT_TOKEN',
};

/**
 * Generates an ID to map strings from translation files
 * When a new endpoint is created, the translation files must be updated
 * with a key in the following format: ALLOW_<authType>
 * @param {string} type the authType
 * @returns {string} translationId
 */
const makeTranslationId = type => `ALLOW_${type}`;

export const endpointOptions = {
  ANY_AUTH: {
    iconPath: KEY_LOGO_PATH,
    value: 'ANY_AUTH',
    order: 0,
  },
  [NO_AUTH]: {
    iconPath: KEY_LOGO_PATH,
    value: NO_AUTH,
    order: 100,
  },
  [BASIC]: {
    iconPath: KEY_LOGO_PATH,
    value: BASIC,
    order: 200,
  },
  [TOKEN]: {
    iconPath: KEY_LOGO_PATH,
    featureFlag: featureFlags[TOKEN],
    value: TOKEN,
    order: 300,
  },
  [OAUTH2]: {
    iconPath: KEY_LOGO_PATH,
    value: OAUTH2,
    order: 400,
  },
  [OAUTH2_AUTH_CODE]: {
    iconPath: KEY_LOGO_PATH,
    value: OAUTH2_AUTH_CODE,
    order: 500,
    featureFlag: featureFlags[OAUTH2_AUTH_CODE],
  },
  [OAUTH2_GOOGLE]: {
    iconPath: '/static/images/icons/google_12.png',
    featureFlag: featureFlags[OAUTH2_GOOGLE],
    value: OAUTH2_GOOGLE,
    order: 600,
  },
  [OAUTH2_MS_GRAPH]: {
    iconPath: '/static/images/logos/MS_GRAPH.svg',
    value: OAUTH2_MS_GRAPH,
    order: 700,
  },
  [REMEDY_TOKEN]: {
    iconPath: '/static/images/icons/bmc_12.png',
    value: REMEDY_TOKEN,
    order: 800,
  },
  [OAUTH2_FORCE]: {
    iconPath: '/static/images/icons/salesforce_12.png',
    value: OAUTH2_FORCE,
    order: 900,
  },
  [SERVICENOW]: {
    iconPath: '/static/images/icons/servicenow_12.png',
    value: SERVICENOW,
    order: 1_000,
  },
  [OAUTH_SLACK_V2]: {
    iconPath: '/static/images/icons/slack_12.png',
    featureFlag: featureFlags[OAUTH_SLACK_V2],
    value: OAUTH_SLACK_V2,
    order: 1_100,
  },
  [OAUTH_SLACK]: {
    iconPath: '/static/images/icons/slack_12.png',
    value: OAUTH_SLACK,
    order: 1_200,
  },
  [OAUTH_ZOOM_S2S]: {
    iconPath: '/static/images/icons/zoom.png',
    featureFlag: featureFlags[OAUTH_ZOOM_S2S],
    value: OAUTH_ZOOM_S2S,
    order: 1_300,
  },
  [OAUTH_ZOOM]: {
    iconPath: '/static/images/icons/zoom.png',
    featureFlag: featureFlags[OAUTH_ZOOM],
    value: OAUTH_ZOOM,
    order: 1_400,
  },
  [XMATTERS]: {
    iconPath: '/static/css/icons/svg/uE0CB-x-icon.svg',
    value: XMATTERS,
    order: 1_500,
  },
  [EBFD]: {
    iconPath: EVERBRIDGE_LOGO_PATH,
    value: EBFD,
    order: 1_600,
  },
};

export const BuiltinEndpointTypes = {
  [XMATTERS]: XMATTERS,
  [EBFD]: EBFD,
};

// const { featureToggleMap } = state.featureToggles;
export const getEndpointOptionByAuthType = ({ featureToggleMap, type }) => {
  const xmattersEndpointSupportEnabled = isXmattersEndpointSupportEnabled({
    featureToggles: { featureToggleMap },
  });
  const { featureFlag } = endpointOptions[type];
  if (!featureFlag || featureToggleMap[featureFlag] === 'true') {
    // COR-31842 Remove when SLACK_OAUTH_V2_ENDPOINT is deprecated
    let translationAuthType = type;
    if (
      type === 'OAUTH_SLACK' &&
      featureToggleMap.SLACK_OAUTH_V2_ENDPOINT === 'false'
    ) {
      translationAuthType = 'OAUTH_SLACK_V1_TEMP';
    }
    if (type === XMATTERS && !xmattersEndpointSupportEnabled) {
      return undefined;
    }
    if (type === EBFD && xmattersEndpointSupportEnabled) {
      return undefined;
    }

    return {
      ...endpointOptions[type],
      translationId: makeTranslationId(translationAuthType),
    };
  }
  return undefined;
};

export const getEndpointOptions = featureToggleMap => {
  const options = [];
  const types = Object.keys(endpointOptions);
  for (let i = 0; i < types.length; i += 1) {
    const option = getEndpointOptionByAuthType({
      featureToggleMap,
      type: types[i],
    });
    if (option) {
      options.push(option);
    }
  }
  return options.sort((a, b) => (a.order > b.order ? 1 : -1));
};

export default {
  BASIC,
  OAUTH2_GOOGLE,
  NO_AUTH,
  OAUTH2,
  OAUTH2_AUTH_CODE,
  OAUTH2_MS_GRAPH,
  REMEDY_TOKEN,
  OAUTH2_FORCE,
  SERVICENOW,
  OAUTH_SLACK,
  OAUTH_SLACK_V2,
  TOKEN,
  XMATTERS,
  OAUTH_ZOOM,
  OAUTH_ZOOM_S2S,
  EBFD,
};
